import {
  AdditiveBlending,
  BufferAttribute,
  BufferGeometry,
  Group,
  Points,
  ShaderMaterial,
  type Camera,
  type Scene,
} from 'three';
import type { PresetEffect } from './types';

const SPARKLE_COUNT = 1200;
const DUST_COUNT = 600;

export class NebulaEffect implements PresetEffect {
  private group: Group | null = null;
  private scene: Scene | null = null;
  private sparkleMat: ShaderMaterial | null = null;
  private dustMat: ShaderMaterial | null = null;

  setup(scene: Scene, _camera: Camera): void {
    this.scene = scene;
    this.group = new Group();
    this.group.name = 'nebulaFx';

    this.group.add(this.buildSparkles());
    this.group.add(this.buildDust());

    scene.add(this.group);
  }

  update(_delta: number, elapsed: number): void {
    if (this.sparkleMat) this.sparkleMat.uniforms.uTime.value = elapsed;
    if (this.dustMat) this.dustMat.uniforms.uTime.value = elapsed;
    if (this.group) {
      this.group.rotation.y = elapsed * 0.012;
      this.group.rotation.x = Math.sin(elapsed * 0.04) * 0.06;
    }
  }

  dispose(): void {
    if (this.group && this.scene) {
      this.scene.remove(this.group);
      this.group.traverse((obj) => {
        if ('geometry' in obj && obj.geometry && typeof (obj.geometry as { dispose?: () => void }).dispose === 'function') {
          (obj.geometry as { dispose: () => void }).dispose();
        }
        if ('material' in obj && obj.material) {
          const mat = obj.material as { dispose?: () => void };
          mat.dispose?.();
        }
      });
    }
    this.group = null;
    this.scene = null;
    this.sparkleMat = null;
    this.dustMat = null;
  }

  private buildSparkles(): Points {
    const positions = new Float32Array(SPARKLE_COUNT * 3);
    const colors = new Float32Array(SPARKLE_COUNT * 3);
    const sizes = new Float32Array(SPARKLE_COUNT);
    const phases = new Float32Array(SPARKLE_COUNT);

    for (let i = 0; i < SPARKLE_COUNT; i++) {
      const r = 6 + Math.random() * 20;
      const theta = Math.random() * Math.PI * 2;
      const phi = Math.acos(2 * Math.random() - 1);
      positions[i * 3 + 0] = r * Math.sin(phi) * Math.cos(theta);
      positions[i * 3 + 1] = r * Math.sin(phi) * Math.sin(theta);
      positions[i * 3 + 2] = r * Math.cos(phi);

      const t = Math.random();
      let r3: number, g3: number, b3: number;
      if (t < 0.35) { r3 = 0.1; g3 = 0.85; b3 = 1.0; }       // cyan
      else if (t < 0.6) { r3 = 1.0; g3 = 0.4; b3 = 0.9; }    // magenta
      else if (t < 0.85) { r3 = 0.95; g3 = 0.95; b3 = 1.0; } // white
      else { r3 = 1.0; g3 = 0.85; b3 = 0.4; }                // gold
      colors[i * 3] = r3; colors[i * 3 + 1] = g3; colors[i * 3 + 2] = b3;

      sizes[i] = 1.5 + Math.random() * 4.5;
      phases[i] = Math.random() * Math.PI * 2;
    }

    const geom = new BufferGeometry();
    geom.setAttribute('position', new BufferAttribute(positions, 3));
    geom.setAttribute('color', new BufferAttribute(colors, 3));
    geom.setAttribute('aSize', new BufferAttribute(sizes, 1));
    geom.setAttribute('aPhase', new BufferAttribute(phases, 1));

    this.sparkleMat = new ShaderMaterial({
      uniforms: { uTime: { value: 0 } },
      vertexShader: SPARKLE_VS,
      fragmentShader: SPARKLE_FS,
      vertexColors: true,
      transparent: true,
      blending: AdditiveBlending,
      depthWrite: false,
    });

    return new Points(geom, this.sparkleMat);
  }

  private buildDust(): Points {
    const positions = new Float32Array(DUST_COUNT * 3);
    const colors = new Float32Array(DUST_COUNT * 3);
    const sizes = new Float32Array(DUST_COUNT);
    const phases = new Float32Array(DUST_COUNT);

    for (let i = 0; i < DUST_COUNT; i++) {
      // Wider, looser shell — these are big soft "dust puffs" giving volumetric feel
      const r = 12 + Math.random() * 28;
      const theta = Math.random() * Math.PI * 2;
      const phi = Math.acos(2 * Math.random() - 1);
      positions[i * 3 + 0] = r * Math.sin(phi) * Math.cos(theta);
      positions[i * 3 + 1] = r * Math.sin(phi) * Math.sin(theta);
      positions[i * 3 + 2] = r * Math.cos(phi);

      // Dimmer, desaturated cosmic tints
      const t = Math.random();
      let r3: number, g3: number, b3: number;
      if (t < 0.5) { r3 = 0.15; g3 = 0.25; b3 = 0.45; }    // blue dust
      else if (t < 0.8) { r3 = 0.4; g3 = 0.15; b3 = 0.35; } // purple dust
      else { r3 = 0.45; g3 = 0.3; b3 = 0.15; }              // amber dust
      colors[i * 3] = r3; colors[i * 3 + 1] = g3; colors[i * 3 + 2] = b3;

      sizes[i] = 30 + Math.random() * 80;
      phases[i] = Math.random() * Math.PI * 2;
    }

    const geom = new BufferGeometry();
    geom.setAttribute('position', new BufferAttribute(positions, 3));
    geom.setAttribute('color', new BufferAttribute(colors, 3));
    geom.setAttribute('aSize', new BufferAttribute(sizes, 1));
    geom.setAttribute('aPhase', new BufferAttribute(phases, 1));

    this.dustMat = new ShaderMaterial({
      uniforms: { uTime: { value: 0 } },
      vertexShader: DUST_VS,
      fragmentShader: DUST_FS,
      vertexColors: true,
      transparent: true,
      blending: AdditiveBlending,
      depthWrite: false,
    });

    return new Points(geom, this.dustMat);
  }
}

const SPARKLE_VS = `
  attribute float aSize;
  attribute float aPhase;
  varying vec3 vColor;
  varying float vTwinkle;
  uniform float uTime;
  void main() {
    vColor = color;
    vTwinkle = 0.5 + 0.5 * sin(uTime * 1.8 + aPhase);
    vec4 mv = modelViewMatrix * vec4(position, 1.0);
    gl_Position = projectionMatrix * mv;
    gl_PointSize = aSize * vTwinkle * (200.0 / -mv.z);
  }
`;

const SPARKLE_FS = `
  varying vec3 vColor;
  varying float vTwinkle;
  void main() {
    vec2 c = gl_PointCoord - 0.5;
    float r = length(c);
    if (r > 0.5) discard;
    // Soft circular glow with star-like center spike
    float core = pow(1.0 - r * 2.0, 3.0);
    float spike = pow(1.0 - r * 2.0, 12.0);
    float a = (core * 0.55 + spike * 0.9) * vTwinkle;
    gl_FragColor = vec4(vColor * (0.6 + 0.4 * vTwinkle), a);
  }
`;

const DUST_VS = `
  attribute float aSize;
  attribute float aPhase;
  varying vec3 vColor;
  varying float vPulse;
  uniform float uTime;
  void main() {
    vColor = color;
    vPulse = 0.7 + 0.3 * sin(uTime * 0.3 + aPhase);
    vec4 mv = modelViewMatrix * vec4(position, 1.0);
    gl_Position = projectionMatrix * mv;
    gl_PointSize = aSize * (100.0 / -mv.z);
  }
`;

const DUST_FS = `
  varying vec3 vColor;
  varying float vPulse;
  void main() {
    vec2 c = gl_PointCoord - 0.5;
    float r = length(c);
    if (r > 0.5) discard;
    // Very soft falloff for cloudy puff appearance
    float a = pow(1.0 - r * 2.0, 2.0) * 0.22 * vPulse;
    gl_FragColor = vec4(vColor, a);
  }
`;
